import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import {
  CurrencyValue,
  DisplaySwitch,
  Flex,
  formatDatetime,
  formatNumber,
  LineChart
} from '@blockanalitica/ui';
import { ExtendedChartOptions } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useCallback, useMemo, useState } from 'react';

export default function PSM3HistoricGraph() {
  const [activeDisplayOption, setActiveDisplayOption] = useState('total');

  const displayOptions = [
    { key: 'total', value: 'Total' },
    { key: 'assets', value: 'Assets' }
  ];

  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi({
    url: `/arbitrum/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const config = updateChartConfigWithPeriod({
    options: { fill: false } as ExtendedChartOptions
  });

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  const yScaleValueFormatter = useMemo(
    () => (value: number) => {
      return `${formatNumber(value)}`;
    },
    []
  );

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'total':
        return [
          {
            label: 'Total Assets',
            xField: 'date',
            yField: 'total_balance'
          }
        ];
      default:
        return [
          {
            label: 'USDS Bridged',
            borderColor: 'rgba(255, 139, 91, 1)',
            xField: 'date',
            yField: 'usds_balance'
          },
          {
            label: 'sUSDS Bridged',
            borderColor: 'rgba(0, 245, 170, 1)',
            xField: 'date',
            yField: 'susds_balance'
          }
        ];
    }
  }, [activeDisplayOption]);

  const valueFormatter = useMemo(() => {
    return function valueFormatter(value: string) {
      switch (activeDisplayOption) {
        case 'total':
          return <CurrencyValue value={value} options={{ currency: 'USD' }} />;
        default:
          return <CurrencyValue value={value} options={{ currency: '' }} />;
      }
    };
  }, [activeDisplayOption]);

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <ChartSkeleton />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="xs">
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      <LineChart
        key={activeDisplayOption}
        data={data}
        datasets={datasets}
        yScaleValueFormatter={yScaleValueFormatter}
        valueFormatter={valueFormatter}
        timeFilter={timeFilter}
        config={config}
        subValueFormatter={subValueFormatter}
      />
    </Flex>
  );
}
